import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import type { Category } from '@shared/types';

export const Route = createFileRoute('/categories')({
  component: CategoriesPage,
});

function CategoriesPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['categories'], queryFn: api.listCategories });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    parentId: '',
    type: 'expense' as 'expense' | 'income' | 'transfer',
    color: '#94a3b8',
    icon: '',
  });

  const tree = useMemo(() => buildTree(data ?? []), [data]);

  const createMut = useMutation({
    mutationFn: () =>
      api.createCategory({
        name: form.name,
        parentId: form.parentId || null,
        type: form.type,
        color: form.color,
        icon: form.icon || null,
      } as Partial<Category>),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Catégorie créée');
      setOpen(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteCategory(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Catégorie supprimée');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Catégories</h1>
          <p className="text-sm text-muted-foreground">Hiérarchie pour ranger tes transactions</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4" /> Nouvelle catégorie
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvelle catégorie</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Nom</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v as typeof form.type }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Dépense</SelectItem>
                      <SelectItem value="income">Revenu</SelectItem>
                      <SelectItem value="transfer">Transfert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Parent</Label>
                  <Select value={form.parentId} onValueChange={(v) => setForm((f) => ({ ...f, parentId: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Aucun" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Aucun</SelectItem>
                      {(data ?? [])
                        .filter((c) => !c.parentId)
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Couleur</Label>
                  <Input type="color" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Icône (lucide)</Label>
                  <Input value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} placeholder="ShoppingCart" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={() => createMut.mutate()} disabled={!form.name}>
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Hiérarchie</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {tree.map((node) => (
            <div key={node.id}>
              <div className="flex items-center justify-between rounded-md p-2 hover:bg-muted/40">
                <div className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ background: node.color ?? 'hsl(var(--muted-fg))' }}
                  />
                  <span className="font-medium">{node.name}</span>
                  {node.isSystem ? <Badge variant="outline">système</Badge> : null}
                </div>
                {!node.isSystem && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (window.confirm(`Supprimer ${node.name} ?`)) deleteMut.mutate(node.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {node.children.length > 0 && (
                <div className="ml-6 border-l border-border pl-4 space-y-1">
                  {node.children.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-md py-1 pr-2 hover:bg-muted/40">
                      <div className="flex items-center gap-2 text-sm">
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: c.color ?? 'hsl(var(--muted-fg))' }}
                        />
                        {c.name}
                      </div>
                      {!c.isSystem && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (window.confirm(`Supprimer ${c.name} ?`)) deleteMut.mutate(c.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

interface TreeNode extends Category {
  children: Category[];
}

function buildTree(list: Category[]): TreeNode[] {
  const roots = list.filter((c) => !c.parentId);
  return roots.map((r) => ({ ...r, children: list.filter((c) => c.parentId === r.id) }));
}
